import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { useMoveOptions, useReasons, useRoster } from '@/api/m3';
import type { OpResult, RegistrationCard, RegistrationRowDto } from '@/api/m3-types';
import { ChoiceField, Option } from '@/components/kit/Controls';
import { DateInput } from '@/components/kit/DateInput';
import { TextField } from '@/components/kit/Field';
import { SectionLabel } from '@/components/kit/Layout';
import { Modal } from '@/components/kit/Modal';
import { Note, Skeleton } from '@/components/kit/Note';
import { OptionList } from '@/components/kit/OptionList';
import { money, mmss } from '@/lib/format';
import { years } from '@/lib/phone';
import { useM3Submit } from '@/features/crm/useM3Submit';

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** «9 000», «−2 000», «2500,50» → число; пусто или мусор — NaN */
export function parseSum(value: string): number {
  const neg = /^\s*[−-]/.test(value);
  const n = Number(value.replace(/[^\d,.]/g, '').replace(',', '.'));
  return value.trim() ? (neg ? -n : n) : NaN;
}

/** Подсказка после операции: освободилось место, а в резерве ждут */
const hintText = (r: OpResult, base: string) => {
  const h = r.hints[0];
  return h ? `${base} Освободилось место — в резерве ждёт «${h.who}».` : base;
};

const METHODS = [
  { value: 'link', label: 'Платёжная ссылка' },
  { value: 'transfer', label: 'Перевод' },
  { value: 'cash', label: 'Наличные' },
] as const;

/**
 * Внести платёж (pay в макете, ТЗ 6.5–6.6). Первый платёж фиксирует цену
 * и занимает место; мест нет — платёж примется с предупреждением.
 */
export function PayModal({ reg, onClose }: { reg: RegistrationCard; onClose: () => void }) {
  const [sum, setSum] = useState(reg.figures.debt ? money(reg.figures.debt) : '');
  const [onDate, setOnDate] = useState<string | null>(todayIso());
  const [method, setMethod] = useState<(typeof METHODS)[number]['value']>('link');
  const amount = parseSum(sum);
  const first = !reg.price.fixed;
  const def = reg.price.defaultPackage;
  const { submit, pending } = useM3Submit(
    () =>
      api.post<OpResult & { fixed: boolean }>(`/registrations/${reg.id}/payments`, {
        amount,
        onDate,
        method,
      }),
    (r) =>
      r.warnings.length
        ? `Платёж принят, но номинация сверх лимита: ${r.warnings[0].paidSeats} из ${r.warnings[0].limit}`
        : `Платёж ${money(amount)} внесён`,
    onClose,
  );
  return (
    <Modal
      title="Внести платёж"
      onClose={onClose}
      confirm={{ label: 'Внести платёж', onClick: submit, disabled: !(amount > 0) || !onDate || pending }}
    >
      <p>
        <b>{reg.who}</b> · {reg.nomination.label} · долг {money(reg.figures.debt)}
      </p>
      <div className="row keep">
        <div className="col">
          <TextField label="Сумма" inputMode="decimal" value={sum} onValue={setSum} />
        </div>
        <div className="col">
          <DateInput label="Дата" value={onDate} onChange={(iso) => setOnDate(iso)} />
        </div>
      </div>
      <ChoiceField label="Способ" items={[...METHODS]} value={method} onChange={setMethod} />
      {first && def ? (
        <Note>
          Это первый платёж: он зафиксирует цену ({reg.price.currentPeriod === 1 ? 'первая' : 'вторая'} цена, {def.name}{' '}
          {money(def.price)}){reg.figures.noSeats ? '.' : ' и займёт место в номинации.'}
        </Note>
      ) : null}
      {reg.figures.noSeats ? (
        <Note tone="warn">
          <b>Лимит набран:</b> оплатили {reg.nomination.paidSeats} из {reg.nomination.limit}. Платёж примется, номинация
          уйдёт сверх лимита — это предупреждение, не запрет.
        </Note>
      ) : null}
    </Modal>
  );
}

/** Номер для возврата — из карточки или из строки реестра отказов */
export interface RefundTarget {
  id: string;
  who: string;
  status: string;
  figures: { paid: number; toRefund: number };
  decline: { reason: string } | null;
}

/** Отметить возврат: не больше оплаченного, основание обязательно */
export function RefundModal({ reg, onClose }: { reg: RefundTarget; onClose: () => void }) {
  const [sum, setSum] = useState(reg.figures.toRefund ? money(reg.figures.toRefund) : '');
  const [onDate, setOnDate] = useState<string | null>(todayIso());
  const [reason, setReason] = useState(
    reg.status === 'declined' && reg.decline ? `Отказ: ${reg.decline.reason}` : reg.status === 'reserve' ? 'Перевод в резерв' : '',
  );
  const amount = parseSum(sum);
  const { submit, pending } = useM3Submit(
    () => api.post<OpResult>(`/registrations/${reg.id}/refunds`, { amount, onDate, reason: reason.trim() }),
    (r) => hintText(r, 'Возврат отмечен.'),
    onClose,
  );
  return (
    <Modal
      title="Отметить возврат"
      onClose={onClose}
      confirm={{ label: 'Отметить возврат', onClick: submit, disabled: !(amount > 0) || !onDate || !reason.trim() || pending }}
    >
      <p>
        <b>{reg.who}</b> · оплачено {money(reg.figures.paid)}
        {reg.figures.toRefund ? ` · к возврату ${money(reg.figures.toRefund)}` : ''}
      </p>
      <div className="row keep">
        <div className="col">
          <TextField label="Сумма" inputMode="decimal" value={sum} onValue={setSum} />
        </div>
        <div className="col">
          <DateInput label="Дата" value={onDate} onChange={(iso) => setOnDate(iso)} />
        </div>
      </div>
      <TextField label="Основание" placeholder="например, переплата" value={reason} onValue={setReason} />
      <Note>Проводит возврат фин. модуль; здесь только отметка, что деньги вернули.</Note>
    </Modal>
  );
}

/** «7,5» — процент без лишних нулей, с запятой */
const pctText = (p: number) => String(Math.round(p * 100) / 100).replace('.', ',');
const parsePct = (v: string) => (v.trim() ? Number(v.replace(/[^\d,.]/g, '').replace(',', '.')) : NaN);

/**
 * Скидка номеру (Road Map п. 15): только уменьшение, без минуса. Два
 * связанных поля — ввёл сумму, процент пересчитался от итога по строкам,
 * и наоборот (итог 40 000: 10 % → 4 000; 3 000 → 7,5 %). Сохраняется сумма,
 * процент — пометкой. Пределы и причину проверяет сервер.
 */
export function DiscountModal({ reg, onClose }: { reg: RegistrationCard; onClose: () => void }) {
  const total = reg.figures.rowsSum;
  const d = reg.figures.discount;
  const [sum, setSum] = useState(d ? money(d) : '');
  const [pct, setPct] = useState(d && reg.figures.discountPct !== null ? pctText(reg.figures.discountPct) : '');
  const [reason, setReason] = useState(reg.figures.discountReason ?? '');
  const amount = sum.trim() ? parseSum(sum) : 0;
  const { submit, pending } = useM3Submit(
    () => api.put(`/registrations/${reg.id}/discount`, { amount, reason: reason.trim() || null }),
    amount ? 'Скидка сохранена, счёт пересчитан' : 'Скидка снята',
    onClose,
  );
  return (
    <Modal
      title="Скидка"
      onClose={onClose}
      confirm={{ label: 'Сохранить', onClick: submit, disabled: Number.isNaN(amount) || (amount > 0 && !reason.trim()) || pending }}
    >
      <p>
        Итог по строкам номера — <b>{money(total)}</b>. Скидка уменьшает счёт; при смене состава она сама не
        пересчитывается. Чтобы снять скидку, сотрите сумму.
      </p>
      <div className="row">
        <div className="col">
          <TextField
            label="Сумма"
            inputMode="decimal"
            placeholder="например, 4 000"
            value={sum}
            onValue={(v) => {
              setSum(v);
              const n = parseSum(v);
              setPct(!v.trim() || Number.isNaN(n) || !total ? '' : pctText((Math.abs(n) * 100) / total));
            }}
          />
        </div>
        <div className="col">
          <TextField
            label="Процент"
            inputMode="decimal"
            placeholder="например, 10"
            value={pct}
            onValue={(v) => {
              setPct(v);
              const p = parsePct(v);
              setSum(!v.trim() || Number.isNaN(p) ? '' : money(Math.round(total * p) / 100));
            }}
          />
        </div>
      </div>
      <TextField label="Причина" placeholder="например, скидка постоянному клиенту" value={reason} onValue={setReason} />
    </Modal>
  );
}

/** Оформить отказ (БЛ 5.19): место освобождается, оплаченное — к возврату */
export function DeclineModal({ reg, onClose }: { reg: RegistrationCard; onClose: () => void }) {
  const reasons = useReasons('registration');
  const [reasonId, setReasonId] = useState<string | null>(null);
  const { submit, pending } = useM3Submit(
    () => api.post<OpResult>(`/registrations/${reg.id}/decline`, { reasonId }),
    (r) => hintText(r, 'Отказ оформлен.'),
    onClose,
  );
  return (
    <Modal
      title="Оформить отказ"
      onClose={onClose}
      confirm={{ label: 'Оформить отказ', onClick: submit, disabled: !reasonId || pending, danger: true }}
    >
      <p>
        <b>{reg.who}</b> · {reg.nomination.label}. Место в номинации освободится.
        {reg.figures.paid ? (
          <>
            {' '}
            Оплаченные <b>{money(reg.figures.paid)}</b> уйдут к возврату.
          </>
        ) : null}
        {reg.reserveWaiting ? ` В резерве ждёт «${reg.reserveWaiting.who}».` : ''}
      </p>
      <OptionList
        value={reasonId}
        onChange={setReasonId}
        options={(reasons.data ?? []).filter((r) => r.isActive).map((r) => ({ value: r.id, title: r.name }))}
      />
      <Note>Переезд на другое мероприятие — не отказ: для него есть «Перенести».</Note>
    </Modal>
  );
}

/**
 * Перенос на другое мероприятие (move в макете, ТЗ 6.7): номер уедет
 * в сделку клиента на выбранном мероприятии; деньги — зачётом, если там
 * есть место.
 */
export function MoveModal({ reg, onClose }: { reg: RegistrationCard; onClose: () => void }) {
  const navigate = useNavigate();
  const options = useMoveOptions(reg.id);
  const [eventId, setEventId] = useState<string | null>(null);
  const [nominationId, setNominationId] = useState<string | null>(null);
  const event = options.data?.events.find((e) => e.id === eventId);
  const { submit, pending } = useM3Submit(
    () => api.post<OpResult & { id: string; status: string; carried: number }>(`/registrations/${reg.id}/move`, { nominationId }),
    (r) =>
      r.status === 'reserve'
        ? 'Перенесено в закрытую номинацию — номер в резерве, деньги остались к возврату в исходной записи'
        : `Номер перенесён${r.carried ? ` · зачтено ${money(r.carried)}` : ''}`,
    (r) => {
      onClose();
      navigate(`/registrations/${r.id}`);
    },
  );
  return (
    <Modal
      title="Перенести на другое мероприятие"
      size="wide"
      onClose={onClose}
      confirm={{ label: 'Перенести', onClick: submit, disabled: !nominationId || pending }}
    >
      <p>
        <b>{reg.who}</b> · {reg.nomination.label} · {reg.event.label}. Номер уедет в сделку клиента на выбранном
        мероприятии; если её нет — она создастся.
      </p>
      {options.isLoading ? (
        <Skeleton width="60%" />
      ) : (
        <>
          <SectionLabel>Куда</SectionLabel>
          <OptionList
            value={eventId}
            onChange={(id) => {
              setEventId(id);
              setNominationId(null);
            }}
            options={(options.data?.events ?? []).map((e) => ({
              value: e.id,
              title: e.label,
              description: [e.draft ? 'черновик' : null, e.dealExists ? 'сделка клиента есть' : 'создастся сделка']
                .filter(Boolean)
                .join(' · '),
            }))}
          />
          {event ? (
            <>
              <SectionLabel>Номинация</SectionLabel>
              <OptionList
                value={nominationId}
                onChange={setNominationId}
                options={event.nominations.map((n) => ({
                  value: n.id,
                  title: n.label,
                  description: `${n.defaultPackage ? `${n.defaultPackage.name} ${money(n.defaultPackage.price)} · ` : ''}оплатили ${n.paidSeats} из ${n.limit} мест${n.willReserve ? ' · номинация закрыта — резерв' : ''}`,
                }))}
              />
            </>
          ) : null}
          {!options.data?.events.length ? <Note tone="warn">Других непрошедших мероприятий с подходящими номинациями нет.</Note> : null}
        </>
      )}
      {reg.figures.paid ? (
        <Note>
          Оплачено <b>{money(reg.figures.paid)}</b>: в этой регистрации — возврат, в новой — платёж «зачёт с переноса». Он
          зафиксирует цену нового мероприятия на сегодня. Если новая номинация закрыта — номер встанет в резерв, а
          деньги останутся к возврату здесь.
        </Note>
      ) : null}
    </Modal>
  );
}

/** Музыка: длительность «м:сс» и подтверждение фонограммы */
export function MusicModal({ reg, onClose }: { reg: RegistrationCard; onClose: () => void }) {
  const [value, setValue] = useState(reg.music.sec !== null ? mmss(reg.music.sec) : '');
  const [confirmed, setConfirmed] = useState(reg.music.confirmed);
  const { submit, pending } = useM3Submit(
    () => api.post(`/registrations/${reg.id}/checklist`, { item: 'music', music: value.trim() || null, confirmed }),
    'Музыка сохранена, хронометраж пересчитан',
    onClose,
  );
  return (
    <Modal title="Музыка" onClose={onClose} confirm={{ label: 'Сохранить', onClick: submit, disabled: pending }}>
      <p>
        Длительность фонограммы, которую прислали на почту. Лимит номинации — {mmss(reg.nomination.durationLimitSec)},
        штраф 0,1 балла за каждую секунду сверх.
      </p>
      <div className="row keep">
        <div className="col fix">
          <TextField label="Длительность" placeholder="м:сс" size={8} value={value} onValue={setValue} />
        </div>
      </div>
      <Option
        checked={confirmed}
        onToggle={() => setConfirmed((v) => !v)}
        title="Фонограмма подтверждена"
        description="прослушана, файл правильный"
      />
    </Modal>
  );
}

/**
 * Участники из состава команды (pickCl в макете): отмеченные займут пустые
 * строки, лишние — новые. В строку `row` — один человек.
 */
export function PickFromTeamModal({
  reg,
  row,
  onClose,
}: {
  reg: RegistrationCard;
  row?: RegistrationRowDto;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const roster = useRoster(reg.id, true);
  const [selected, setSelected] = useState<string[]>([]);
  const one = Boolean(row);
  const emptyRows = useMemo(
    () => reg.rows.filter((r) => !r.withdrawn && !r.client && !r.lastName && !r.firstName && !r.birthDate).length,
    [reg.rows],
  );
  const pickOne = useM3Submit(
    () => api.patch(`/registration-rows/${row!.id}`, { clientId: selected[0] }),
    `Строка ${row?.position ?? ''}: участник из состава`,
    onClose,
  );
  const pickMany = useM3Submit(
    () => api.post<{ added: number; newRows: number }>(`/registrations/${reg.id}/rows/from-team`, { clientIds: selected }),
    (r) => `Добавлено из состава: ${r.added}${r.newRows ? `, новых строк ${r.newRows} — счёт пересчитан` : ''}`,
    onClose,
  );
  const extra = selected.length - emptyRows;
  return (
    <Modal
      title={one ? `Строка ${row!.position}: участник из состава` : `Состав команды «${reg.team?.name ?? ''}»`}
      size="wide"
      onClose={onClose}
      confirm={{
        label: one ? 'Поставить в строку' : `Добавить в номер${selected.length ? ` · ${selected.length}` : ''}`,
        onClick: one ? pickOne.submit : pickMany.submit,
        disabled: !selected.length || pickOne.pending || pickMany.pending,
      }}
    >
      <p>
        {one ? 'Выберите, кто встанет в эту строку.' : 'Отметьте, кто выступает в номере.'} В номер участники попадают из
        состава команды, а в состав — из базы клиентов в карточке команды.
      </p>
      {roster.isLoading ? (
        <Skeleton width="60%" />
      ) : roster.data?.length ? (
        <div className="olist">
          {roster.data.map((m) => (
            <Option
              key={m.client.id}
              radio={one}
              disabled={m.inNumber}
              checked={selected.includes(m.client.id)}
              onToggle={() =>
                setSelected((s) => (one ? [m.client.id] : s.includes(m.client.id) ? s.filter((x) => x !== m.client.id) : [...s, m.client.id]))
              }
              title={m.client.fullName}
              description={
                <>
                  {m.birthDate ? m.birthDate.split('-').reverse().join('.') : 'дата рождения не указана'}
                  {m.ageAtEvent !== null ? ` · ${years(m.ageAtEvent)} на дату выступления` : ''}
                  {m.outOfAge ? <span className="wtx"> · вне возраста номинации</span> : null}
                  {m.inNumber ? ' · уже в номере' : ''}
                </>
              }
            />
          ))}
        </div>
      ) : (
        <Note>В составе команды никого нет.</Note>
      )}
      <Note>
        Нет нужного человека — добавьте его в состав в{' '}
        <span className="lnk" onClick={() => reg.team && navigate(`/teams/${reg.team.id}`)}>
          карточке команды
        </span>
        . Или впишите фамилию, имя и дату рождения в строку вручную.
      </Note>
      {!one && selected.length ? (
        <Note>
          Займут пустые строки ({emptyRows})
          {extra > 0 ? `, ещё строк добавится: ${extra} — счёт пересчитается` : ''}.
        </Note>
      ) : null}
    </Modal>
  );
}

/** Снять участника (БЛ 5.14а): строка останется зачёркнутой, за него — к возврату */
export function WithdrawModal({ reg, row, onClose }: { reg: RegistrationCard; row: RegistrationRowDto; onClose: () => void }) {
  const reasons = useReasons('participant');
  const [reasonId, setReasonId] = useState<string | null>(null);
  const name = [row.lastName, row.firstName].filter(Boolean).join(' ') || `строка ${row.position}`;
  const ref = Math.max(0, reg.figures.paid - (reg.figures.billed - row.price)) - reg.figures.toRefund;
  const { submit, pending } = useM3Submit(
    () => api.post(`/registration-rows/${row.id}/withdraw`, { reasonId }),
    ref > 0 ? `${name} снят(а) с номера · к возврату ${money(ref)}` : `${name} снят(а) с номера, счёт уменьшен`,
    onClose,
  );
  return (
    <Modal title="Снять участника" onClose={onClose} confirm={{ label: 'Снять участника', onClick: submit, disabled: !reasonId || pending }}>
      <p>
        <b>{name}</b> не выступит в номере «{reg.who}». Строка останется в номере зачёркнутой — для истории, в счёт,
        состав и проверки она больше не входит.
      </p>
      <OptionList
        value={reasonId}
        onChange={setReasonId}
        options={(reasons.data ?? []).filter((r) => r.isActive).map((r) => ({ value: r.id, title: r.name }))}
      />
      <Note>
        {ref > 0 ? (
          <>
            Возврат 100%: <b>{money(ref)}</b> — цена пакета «{row.package.name}». Появится «к возврату», отметить возврат —
            кнопкой в карточке.
          </>
        ) : (
          `Номер ещё не оплачен за этого участника — счёт просто уменьшится на ${money(row.price)}.`
        )}
      </Note>
    </Modal>
  );
}

/** Подтверждение необратимого: «В резерв», «Убрать строку» (docs/m3-resheniya.md) */
export function ConfirmModal({
  title,
  text,
  label,
  run,
  done,
  onClose,
}: {
  title: string;
  text: string;
  label: string;
  run: () => Promise<OpResult | unknown>;
  done: string;
  onClose: () => void;
}) {
  const { submit, pending } = useM3Submit(run, (r) => (r && typeof r === 'object' && 'hints' in r ? hintText(r as OpResult, done) : done), onClose);
  return (
    <Modal title={title} onClose={onClose} confirm={{ label, onClick: submit, disabled: pending, danger: true }}>
      <p>{text}</p>
    </Modal>
  );
}
